import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, ChevronUp, ChevronDown, Trash2, Save, Loader2,
  Clock, Filter, Globe, ShieldCheck, Zap, GripVertical, X,
  Activity, ArrowRight, AlertTriangle, Copy, Send, FlaskConical, CheckCircle2, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PageIntro, Status } from '@/workspace/ui';
import './automation-controls.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ─────────────────────────── Constants ───────────────────────────
const STEP_TYPES = [
  { type: 'wait_for', label: 'Wait For', icon: ShieldCheck, color: '#059669', desc: 'Wait for required fields' },
  { type: 'filter',   label: 'Filter',   icon: Filter,      color: '#7a7b77', desc: 'Apply conditions' },
  { type: 'delay',    label: 'Delay',    icon: Clock,       color: '#7a7b77', desc: 'Wait then refetch' },
  { type: 'webhook',  label: 'Webhook',  icon: Globe,       color: '#3867ef', desc: 'Send to URL' },
  { type: 'capi',     label: 'Facebook CAPI', icon: Send,   color: '#3867ef', desc: 'Send conversion to Meta' },
  { type: 'ghl',      label: 'GoHighLevel', icon: Users,    color: '#3867ef', desc: 'Upsert contact to GHL' },
];

const CAPI_EVENT_OPTIONS = ['Purchase', 'Lead', 'CompleteRegistration', 'InitiateCheckout', 'AddToCart', 'Subscribe'];

const CAPI_ACTION_SOURCES = [
  { value: 'website',         label: 'Website' },
  { value: 'email',           label: 'Email' },
  { value: 'phone_call',      label: 'Phone call' },
  { value: 'chat',            label: 'Chat' },
  { value: 'physical_store',  label: 'Physical store' },
  { value: 'system_generated', label: 'System generated' },
  { value: 'other',           label: 'Other' },
];

// Meta CAPI target paths a mapping row can write to. Grouped so the picker
// reads like Meta's own parameter reference; "Custom path…" escapes to any
// dot-path (e.g. custom_data.store_data.store_code) for the rest.
const CAPI_TARGET_PATHS = [
  { value: 'user_data.em',                 label: 'user_data.em — Email' },
  { value: 'user_data.ph',                 label: 'user_data.ph — Phone' },
  { value: 'user_data.fn',                 label: 'user_data.fn — First name' },
  { value: 'user_data.ln',                 label: 'user_data.ln — Last name' },
  { value: 'user_data.external_id',        label: 'user_data.external_id' },
  { value: 'user_data.ge',                 label: 'user_data.ge — Gender' },
  { value: 'user_data.db',                 label: 'user_data.db — Date of birth' },
  { value: 'user_data.ct',                 label: 'user_data.ct — City' },
  { value: 'user_data.st',                 label: 'user_data.st — State' },
  { value: 'user_data.zp',                 label: 'user_data.zp — Zip' },
  { value: 'user_data.country',            label: 'user_data.country' },
  { value: 'user_data.client_ip_address',  label: 'user_data.client_ip_address' },
  { value: 'user_data.client_user_agent',  label: 'user_data.client_user_agent' },
  { value: 'user_data.fbc',                label: 'user_data.fbc — Facebook click ID' },
  { value: 'user_data.fbp',                label: 'user_data.fbp — Facebook browser ID' },
  { value: 'custom_data.value',            label: 'custom_data.value' },
  { value: 'custom_data.currency',         label: 'custom_data.currency' },
  { value: 'custom_data.content_name',     label: 'custom_data.content_name' },
  { value: 'custom_data.order_id',         label: 'custom_data.order_id' },
  { value: 'action_source',                label: 'action_source (overrides the setting above)' },
  { value: 'event_source_url',             label: 'event_source_url (overrides the setting above)' },
];

// GoHighLevel /contacts/upsert target paths a mapping row can write to.
const GHL_TARGET_PATHS = [
  { value: 'email',                        label: 'email' },
  { value: 'firstName',                    label: 'firstName' },
  { value: 'lastName',                     label: 'lastName' },
  { value: 'phone',                        label: 'phone' },
  { value: 'companyName',                  label: 'companyName' },
  { value: 'address1',                     label: 'address1' },
  { value: 'city',                         label: 'city' },
  { value: 'state',                        label: 'state' },
  { value: 'postalCode',                   label: 'postalCode' },
  { value: 'country',                      label: 'country' },
  { value: 'website',                      label: 'website' },
  { value: 'source',                       label: 'source' },
  { value: 'attributionSource.url',        label: 'attributionSource.url' },
  { value: 'attributionSource.fbclid',     label: 'attributionSource.fbclid' },
  { value: 'attributionSource.ip',         label: 'attributionSource.ip' },
  { value: 'attributionSource.userAgent',  label: 'attributionSource.userAgent' },
];


const FILTER_FIELDS = [
  { value: 'email',       label: 'Email' },
  { value: 'phone',       label: 'Phone' },
  { value: 'name',        label: 'Name' },
  { value: 'utm_source',  label: 'UTM Source' },
  { value: 'utm_campaign',label: 'UTM Campaign' },
  { value: 'utm_medium',  label: 'UTM Medium' },
  { value: 'fbclid',      label: 'Facebook Click ID' },
  { value: 'gclid',       label: 'Google Click ID' },
  { value: 'campaign_id', label: 'Campaign ID' },
  { value: 'adset_id',    label: 'Ad Set ID' },
  { value: 'client_ip',   label: 'IP Address' },
  { value: 'tags',        label: 'Tags' },
];

const OPERATORS = [
  { value: 'exists',     label: 'exists' },
  { value: 'not_exists', label: 'does not exist' },
  { value: 'equals',     label: '= equals' },
  { value: 'not_equals', label: '≠ not equals' },
  { value: 'contains',   label: '~ contains' },
];

const TETHER_FIELDS = [
  { value: 'email',        label: 'Email' },
  { value: 'name',         label: 'Full Name' },
  { value: 'first_name',   label: 'First Name' },
  { value: 'last_name',    label: 'Last Name' },
  { value: 'phone',        label: 'Phone' },
  { value: 'contact_id',   label: 'Contact ID' },
  { value: 'client_ip',    label: 'IP Address' },
  { value: 'user_agent',   label: 'User Agent (FB CAPI)' },
  { value: 'fbc',          label: 'Facebook Click ID (fbc)' },
  { value: 'fbp',          label: 'Facebook Browser ID (fbp)' },
  { value: 'created_at',   label: 'First Seen' },
  { value: 'updated_at',   label: 'Last Updated' },
  { value: 'utm_source',   label: 'UTM Source' },
  { value: 'utm_medium',   label: 'UTM Medium' },
  { value: 'utm_campaign', label: 'UTM Campaign' },
  { value: 'utm_term',     label: 'UTM Term' },
  { value: 'utm_content',  label: 'UTM Content' },
  { value: 'utm_id',       label: 'UTM ID' },
  { value: 'campaign_id',  label: 'Campaign ID' },
  { value: 'adset_id',     label: 'Ad Set ID' },
  { value: 'ad_id',        label: 'Ad ID' },
  { value: 'fbclid',       label: 'Facebook Click ID' },
  { value: 'gclid',        label: 'Google Click ID (gclid)' },
  { value: 'wbraid',       label: 'Google Web Click ID (wbraid)' },
  { value: 'gbraid',       label: 'Google App Click ID (gbraid)' },
  { value: 'ttclid',       label: 'TikTok Click ID (ttclid)' },
  { value: 'msclkid',      label: 'Bing Click ID (msclkid)' },
  { value: 'twclid',       label: 'X/Twitter Click ID (twclid)' },
  { value: 'li_fat_id',    label: 'LinkedIn Click ID (li_fat_id)' },
  { value: 'epik',         label: 'Pinterest Click ID (epik)' },
  { value: 'sccid',        label: 'Snapchat Click ID (sccid)' },
  { value: 'source_link_tag', label: 'Source Link Tag' },
];

// Source side of a CAPI mapping row: every Tether contact field, plus the
// triggering sale's fields (only meaningful for Purchase-type events).
const CAPI_SOURCE_FIELDS = [
  ...TETHER_FIELDS,
  { value: 'sale_amount',    label: 'Sale — Amount' },
  { value: 'sale_currency',  label: 'Sale — Currency' },
  { value: 'sale_product',   label: 'Sale — Product' },
  { value: 'sale_order_id',  label: 'Sale — Order ID' },
];

function uuid4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ─────────────────────────── Step Editors ───────────────────────────

/** Wait For Step Editor */
function WaitForEditor({ config, onChange }) {
  const fields = config?.fields || ['email'];
  const waitTags = config?.tags || [];
  // Existing contact tags feed the wait-for-tag dropdown
  const { data: tagData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => fetch(`${API}/tags`).then(r => (r.ok ? r.json() : { tags: [] })),
    staleTime: 30_000,
  });
  const addableTags = (tagData?.tags || []).filter(t => !waitTags.includes(t));
  const toggleField = (field) => {
    const newFields = fields.includes(field)
      ? fields.filter(f => f !== field)
      : [...fields, field];
    onChange({ ...config, fields: newFields });
  };

  return (
    <div>
      <p className="text-sm mb-3" style={{ color: 'var(--text-dim)' }}>
        Automation pauses until <strong>all selected fields and tags</strong> are present on the
        contact — it re-checks whenever new data arrives (webhooks included), so late tags aren't missed.
      </p>
      <div className="flex flex-wrap gap-2">
        {['email', 'phone', 'name'].map(field => {
          const active = fields.includes(field);
          return (
            <button
              key={field}
              type="button" aria-pressed={active}
              onClick={() => toggleField(field)}
              data-testid={`wait-for-field-${field}`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border transition-all duration-150 text-sm font-semibold"
              style={{
                backgroundColor: active ? 'var(--lime-tint)' : 'var(--surface-2)',
                borderColor:     active ? '#059669' : 'var(--stroke)',
                color:           active ? '#065f46' : 'var(--text-muted)',
              }}
            >
              <span className="w-4 h-4 rounded flex items-center justify-center text-xs border transition-all"
                style={{ backgroundColor: active ? '#059669' : 'transparent', borderColor: active ? '#059669' : 'var(--stroke)', color: '#fff' }}>
                {active && '✓'}
              </span>
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </button>
          );
        })}
      </div>
      <p className="text-xs mt-4 mb-2 font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
        Also wait for tag <span className="font-normal normal-case">(optional — e.g. the webhook's <code>registered</code> tag)</span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {waitTags.map(t => (
          <span key={t} className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-sm font-semibold"
            style={{ backgroundColor: 'var(--lime-tint)', borderColor: '#059669', color: '#065f46' }}>
            {t}
            <button onClick={() => onChange({ ...config, tags: waitTags.filter(x => x !== t) })}
              style={{ color: '#059669' }} data-testid={`wait-for-tag-remove-${t}`} aria-label={`Remove required tag ${t}`}>
              <X size={12} />
            </button>
          </span>
        ))}
        {addableTags.length > 0 && (
          <Select value="" onValueChange={t => onChange({ ...config, tags: [...waitTags, t] })}>
            <SelectTrigger className="h-9 text-sm w-44" style={{ borderColor: 'var(--stroke)' }} data-testid="wait-for-tag-select" aria-label="Add a required tag">
              <SelectValue placeholder="add a tag…" />
            </SelectTrigger>
            <SelectContent className="sp-automation-select">
              {addableTags.map(t => <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {fields.length === 0 && waitTags.length === 0 && (
        <p className="text-xs mt-2" style={{ color: '#dc2626' }}>⚠ Select at least one field or tag to wait for</p>
      )}
    </div>
  );
}

/** Filter Step Editor */
function FilterEditor({ config, onChange }) {
  const filters = config?.filters || [];
  // Existing contact tags feed the tag-value dropdown
  const { data: tagData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => fetch(`${API}/tags`).then(r => (r.ok ? r.json() : { tags: [] })),
    staleTime: 30_000,
  });
  const existingTags = tagData?.tags || [];

  const addFilter = () => {
    onChange({
      ...config,
      filters: [...filters, { id: uuid4(), field: 'utm_source', operator: 'equals', value: '' }]
    });
  };

  const updateFilter = (id, patch) => {
    onChange({
      ...config,
      filters: filters.map(f => f.id === id ? { ...f, ...patch } : f)
    });
  };

  const removeFilter = (id) => {
    onChange({
      ...config,
      filters: filters.filter(f => f.id !== id)
    });
  };

  return (
    <div>
      <p className="text-sm mb-3" style={{ color: 'var(--text-dim)' }}>
        Only contacts matching <strong>all conditions</strong> proceed to the next step.
      </p>
      {filters.length === 0 ? (
        <p className="text-sm mb-3 italic" style={{ color: 'var(--text-dim)' }}>No conditions — all contacts pass through.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {filters.map((f, idx) => {
            const needsValue = !['exists', 'not_exists'].includes(f.operator);
            return (
              <div key={f.id} className="flex items-center gap-2 flex-wrap">
                {idx > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border" style={{ backgroundColor: 'var(--surface-2)', borderColor: 'var(--stroke)', color: 'var(--text-dim)' }}>AND</span>}
                <Select value={f.field} onValueChange={v => updateFilter(f.id, { field: v, ...(v === 'tags' ? { operator: 'contains', value: '' } : {}) })}>
                  <SelectTrigger className="h-9 text-sm w-36" style={{ borderColor: 'var(--stroke)' }} data-testid={`filter-field-${f.id}`} aria-label={`Condition ${idx + 1} field`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="sp-automation-select">
                    {FILTER_FIELDS.map(ff => <SelectItem key={ff.value} value={ff.value} className="text-sm">{ff.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={f.operator} onValueChange={v => updateFilter(f.id, { operator: v, value: '' })}>
                  <SelectTrigger className="h-9 text-sm w-36" style={{ borderColor: 'var(--stroke)' }} data-testid={`filter-operator-${f.id}`} aria-label={`Condition ${idx + 1} operator`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="sp-automation-select">
                    {OPERATORS.map(o => <SelectItem key={o.value} value={o.value} className="text-sm">{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {needsValue && (f.field === 'tags' && existingTags.length > 0 ? (
                  <Select value={f.value || ''} onValueChange={v => updateFilter(f.id, { value: v })}>
                    <SelectTrigger className="h-9 text-sm flex-1 min-w-[120px]" style={{ borderColor: 'var(--stroke)' }} data-testid={`filter-value-${f.id}`} aria-label={`Condition ${idx + 1} value`}>
                      <SelectValue placeholder="pick a tag" />
                    </SelectTrigger>
                    <SelectContent className="sp-automation-select">
                      {existingTags.map(t => <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={f.value || ''}
                    onChange={e => updateFilter(f.id, { value: e.target.value })}
                    placeholder="value"
                    className="h-9 text-sm flex-1 min-w-[120px]"
                    style={{ borderColor: 'var(--stroke)' }}
                    data-testid={`filter-value-${f.id}`} aria-label={`Condition ${idx + 1} value`}
                  />
                ))}
                <button
                  onClick={() => removeFilter(f.id)}
                  className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors hover:bg-red-50"
                  style={{ color: '#dc2626' }}
                  data-testid={`filter-remove-${f.id}`} aria-label={`Remove condition ${idx + 1}`}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <button
        onClick={addFilter}
        className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-sm transition-colors"
        style={{ color: '#7a7b77', backgroundColor: 'rgba(70,100,27,0.06)', border: '1.5px dashed rgba(70,100,27,0.25)' }}
        data-testid="add-filter-condition"
      >
        <Plus size={13} /> Add condition
      </button>
    </div>
  );
}

/** Delay Step Editor */
function CapiEditor({ config, onChange }) {
  const [showToken, setShowToken] = useState(false);
  const set = (patch) => onChange({ ...config, ...patch });

  const fieldMap = config?.field_map || [];
  const addMapping = () => set({ field_map: [...fieldMap, { id: uuid4(), source: 'email', target: 'user_data.em', literal: false }] });
  const updateMapping = (id, patch) => set({ field_map: fieldMap.map(m => m.id === id ? { ...m, ...patch } : m) });
  const removeMapping = (id) => set({ field_map: fieldMap.filter(m => m.id !== id) });

  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
        Sends a server-side conversion to Meta with everything Tether knows about the contact —
        hashed email/phone/name, <code>fbc</code>/<code>fbp</code>, IP and user agent — for maximum
        match quality. Every send is recorded in this automation's run history.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Pixel ID</label>
          <Input
            value={config?.pixel_id || ''}
            onChange={e => set({ pixel_id: e.target.value.replace(/\D/g, '') })}
            placeholder="1234567890123456"
            className="h-9 text-sm"
            style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
            data-testid="capi-pixel-input" aria-label="Meta Pixel ID"
          />
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            Events Manager → your pixel → Settings
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Event</label>
          <Select
            value={CAPI_EVENT_OPTIONS.includes(config?.event_name) ? config.event_name : '__custom__'}
            onValueChange={v => set({ event_name: v === '__custom__' ? (CAPI_EVENT_OPTIONS.includes(config?.event_name) ? 'MyCustomConversion' : config?.event_name || 'MyCustomConversion') : v })}
          >
            <SelectTrigger className="h-9 text-sm" style={{ borderColor: 'var(--stroke)' }} data-testid="capi-event-select" aria-label="Meta event">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="sp-automation-select">
              {CAPI_EVENT_OPTIONS.map(ev => <SelectItem key={ev} value={ev} className="text-sm">{ev}</SelectItem>)}
              <SelectItem value="__custom__" className="text-sm">Custom conversion…</SelectItem>
            </SelectContent>
          </Select>
          {!CAPI_EVENT_OPTIONS.includes(config?.event_name) && (
            <Input
              value={config?.event_name || ''}
              onChange={e => set({ event_name: e.target.value })}
              placeholder="MyCustomConversion"
              className="h-8 text-sm mt-1.5"
              style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
              data-testid="capi-custom-event-input" aria-label="Meta custom event name"
            />
          )}
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            {CAPI_EVENT_OPTIONS.includes(config?.event_name)
              ? 'Purchase auto-includes value & currency from the linked sale'
              : 'Custom events appear under this exact name in Events Manager (letters, numbers, _ or -)'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Action source</label>
          <Select value={config?.action_source || 'website'} onValueChange={v => set({ action_source: v })}>
            <SelectTrigger className="h-9 text-sm" style={{ borderColor: 'var(--stroke)' }} data-testid="capi-action-source-select" aria-label="Meta action source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="sp-automation-select">
              {CAPI_ACTION_SOURCES.map(o => <SelectItem key={o.value} value={o.value} className="text-sm">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            Which channel this conversion happened on — Meta's category restrictions can apply
            differently per channel on the same dataset.
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Event source URL <span className="font-normal" style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <Input
            value={config?.event_source_url || ''}
            onChange={e => set({ event_source_url: e.target.value })}
            placeholder="auto — the contact's last tracked page"
            className="h-9 text-sm"
            style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
            data-testid="capi-event-source-url-input" aria-label="Meta event source URL"
          />
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            Leave blank to use the contact's last tracked page.
          </p>
        </div>
      </div>

      {(config?.event_name || 'Purchase') === 'Purchase' && (
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Combine purchases <span className="font-normal" style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0"
              max="120"
              value={config?.combine_window ?? 0}
              onChange={e => set({ combine_window: Math.max(0, Math.min(120, parseInt(e.target.value) || 0)) })}
              className="h-9 text-sm w-24"
              style={{ borderColor: 'var(--stroke)' }}
              data-testid="capi-combine-input" aria-label="Combine purchases window in minutes"
            />
            <span className="text-sm" style={{ color: 'var(--text-dim)' }}>minutes</span>
            {Number(config?.combine_window) > 0 && (
              <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--lime-tint)', color: 'var(--brand-red)', border: '1px solid var(--lime-border)' }}>
                upsells merge into one conversion
              </span>
            )}
          </div>
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            {Number(config?.combine_window) > 0
              ? `The first purchase waits ${config.combine_window} min, then ONE Purchase is sent with the combined value of every sale in that window — order bumps and upsells don't count as separate conversions. Match it to how long your upsell flow takes.`
              : '0 = every sale sends its own Purchase event. Set a window (e.g. 15 min for a 3-step upsell path) to merge a funnel\'s purchases into one conversion with the summed value.'}
          </p>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Access token</label>
        <div className="flex items-center gap-2">
          <Input
            type={showToken ? 'text' : 'password'}
            value={config?.access_token || ''}
            onChange={e => set({ access_token: e.target.value.trim() })}
            placeholder="EAAxxxxxxxx…"
            className="h-9 text-sm flex-1"
            style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
            data-testid="capi-token-input" aria-label="Meta access token"
          />
          <button type="button" onClick={() => setShowToken(v => !v)}
            className="text-xs px-2 h-9 rounded-md" style={{ color: 'var(--text-muted)', border: '1px solid var(--stroke)' }}>
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
          Events Manager → your pixel → Settings → <b>Conversions API</b> → "Generate access token".
          Stored encrypted with your automation; never shown in run history.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Test event code <span className="font-normal" style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <Input
            value={config?.test_event_code || ''}
            onChange={e => set({ test_event_code: e.target.value.trim() })}
            placeholder="TEST12345"
            className="h-9 text-sm"
            style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
            data-testid="capi-test-code-input" aria-label="Meta test event code"
          />
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            From Events Manager → Test events — verifies delivery live, remove for production
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Step name <span className="font-normal" style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <Input
            value={config?.name || ''}
            onChange={e => set({ name: e.target.value })}
            placeholder="Send purchase to Meta"
            aria-label="Meta step name"
            className="h-9 text-sm"
            style={{ borderColor: 'var(--stroke)' }}
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
          Field mapping <span className="font-normal" style={{ color: 'var(--text-dim)' }}>(optional)</span>
        </label>
        <p className="text-xs mb-2" style={{ color: 'var(--text-dim)' }}>
          Tether always sends its default match keys (email→em, phone→ph, name→fn/ln, IP/UA,
          fbc/fbp, and the sale's value/currency on Purchase). Each row adds or overrides one Meta
          parameter — e.g. a <strong>Static</strong> price → <code>custom_data.value</code> and
          <strong>Static</strong> USD → <code>custom_data.currency</code> when your sale webhook
          carries no price. Nested targets like <code>custom_data.store_data.store_code</code> work too.
        </p>
        {fieldMap.length > 0 && (
          <div className="space-y-2 mb-2">
            {fieldMap.map(m => {
              const isPreset = CAPI_TARGET_PATHS.some(p => p.value === m.target);
              return (
                <div key={m.id} className="flex items-center gap-2 flex-wrap">
                  <button type="button"
                    onClick={() => updateMapping(m.id, { literal: !m.literal, source: m.literal ? 'email' : '' })}
                    className="text-[10px] font-bold uppercase tracking-wide px-2 h-9 rounded-md shrink-0"
                    style={{ border: '1px solid var(--stroke)', color: m.literal ? '#1877F2' : 'var(--text-muted)', backgroundColor: m.literal ? 'rgba(24,119,242,0.06)' : 'transparent' }}
                    title="Toggle between a contact field and a fixed static value">
                    {m.literal ? 'Static' : 'Field'}
                  </button>
                  {m.literal ? (
                    <Input
                      value={m.source || ''}
                      onChange={e => updateMapping(m.id, { source: e.target.value })}
                      placeholder="type a value, e.g. USD or 99.99"
                      className="h-9 text-sm w-40"
                      style={{ borderColor: 'var(--stroke)' }}
                      data-testid={`capi-map-source-${m.id}`} aria-label="Meta mapping source"
                    />
                  ) : (
                    <Select value={m.source} onValueChange={v => updateMapping(m.id, { source: v })}>
                      <SelectTrigger className="h-9 text-sm w-44" style={{ borderColor: 'var(--stroke)' }} data-testid={`capi-map-source-${m.id}`} aria-label="Meta mapping source">
                        <SelectValue placeholder="Tether field" />
                      </SelectTrigger>
                      <SelectContent className="sp-automation-select">
                        {CAPI_SOURCE_FIELDS.map(f => <SelectItem key={f.value} value={f.value} className="text-sm">{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <ArrowRight size={14} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                  <Select
                    value={isPreset ? m.target : '__custom__'}
                    onValueChange={v => updateMapping(m.id, { target: v === '__custom__' ? (isPreset ? '' : m.target) : v })}
                  >
                    <SelectTrigger className="h-9 text-sm w-56" style={{ borderColor: 'var(--stroke)' }} data-testid={`capi-map-target-${m.id}`} aria-label="Meta mapping target">
                      <SelectValue placeholder="Meta parameter" />
                    </SelectTrigger>
                    <SelectContent className="sp-automation-select">
                      {CAPI_TARGET_PATHS.map(p => <SelectItem key={p.value} value={p.value} className="text-sm">{p.label}</SelectItem>)}
                      <SelectItem value="__custom__" className="text-sm">Custom path…</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isPreset && (
                    <Input
                      value={m.target || ''}
                      onChange={e => updateMapping(m.id, { target: e.target.value })}
                      placeholder="custom_data.store_data.store_code"
                      className="h-9 text-sm w-56"
                      style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
                      data-testid={`capi-map-target-custom-${m.id}`} aria-label="Meta mapping custom target path"
                    />
                  )}
                  <button onClick={() => removeMapping(m.id)}
                    className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
                    style={{ color: 'var(--text-dim)', backgroundColor: '#f8f6f2' }}
                    data-testid={`capi-map-remove-${m.id}`} aria-label={`Remove Meta mapping to ${m.target || "unnamed field"}`}>
                    <X size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <button onClick={addMapping}
          className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"
          style={{ color: '#3867ef', backgroundColor: 'rgba(24,119,242,0.06)', border: '1px dashed rgba(24,119,242,0.25)' }}
          data-testid="capi-map-add">
          <Plus size={11} /> Add mapping
        </button>
      </div>

      <div className="rounded-md px-3.5 py-3" style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke-soft)' }}>
        <p className="text-[11px] font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>
          {fieldMap.length > 0
            ? "What gets sent — Tether's default mapping, plus these rows (a row replaces a default key with the same path):"
            : "What gets sent — Tether's default mapping:"}
        </p>
        {fieldMap.length > 0 ? (
          <>
            <pre className="text-[11px] leading-relaxed overflow-x-auto m-0"
              style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
{fieldMap.map(m => `${m.literal ? `"${m.source}"` : m.source || '—'}  →  ${m.target || '—'}`).join('\n')}
            </pre>
            <p className="text-[11px] mt-2 mb-0" style={{ color: 'var(--text-dim)' }}>
              Rows with an empty value are skipped. Every send — the exact payload and Meta's
              response — is recorded under this automation's <b>Runs</b>.
            </p>
          </>
        ) : (
          <>
            <pre className="text-[11px] leading-relaxed overflow-x-auto m-0"
              style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
{`{
  event_name:       "${config?.event_name || 'Purchase'}"
  event_time:       when this step fires
  event_id:         unique run id — Meta dedupes against your browser pixel
  event_source_url: ${config?.event_source_url || "the contact's last tracked page"}
  action_source:    "${config?.action_source || 'website'}"
  user_data: {
    em / ph / fn / ln:   SHA-256 of the contact's email, phone, first/last name
    external_id:         SHA-256 of their Tether contact id
    fbc / fbp:           their Facebook click id + browser id (captured by the tag)
    client_ip_address + client_user_agent
  }${(config?.event_name || 'Purchase') === 'Purchase' ? `
  custom_data: { value, currency, content_name }  ← ${Number(config?.combine_window) > 0 ? `combined total of all sales in the ${config.combine_window}-minute window` : 'from the triggering sale'}` : ''}
}`}
            </pre>
            <p className="text-[11px] mt-2 mb-0" style={{ color: 'var(--text-dim)' }}>
              Fields Tether doesn't have for a contact are omitted (Meta's recommendation). Every send —
              the exact payload and Meta's response — is recorded under this automation's <b>Runs</b>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** GoHighLevel Step Editor */
function GhlEditor({ config, onChange }) {
  const [showToken, setShowToken] = useState(false);
  const set = (patch) => onChange({ ...config, ...patch });

  const fieldMap = config?.field_map || [];
  const addMapping = () => set({ field_map: [...fieldMap, { id: uuid4(), source: 'email', target: 'email', literal: false }] });
  const updateMapping = (id, patch) => set({ field_map: fieldMap.map(m => m.id === id ? { ...m, ...patch } : m) });
  const removeMapping = (id) => set({ field_map: fieldMap.filter(m => m.id !== id) });

  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
        Upserts this contact into GoHighLevel via <code>/contacts/upsert</code>. Unlike Meta's
        Conversions API, there is no sandbox/test mode — every send, including a manual Test,
        performs a real upsert.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Location ID</label>
          <Input
            value={config?.location_id || ''}
            onChange={e => set({ location_id: e.target.value.trim() })}
            placeholder="YOUR_LOCATION_ID"
            className="h-9 text-sm"
            style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
            data-testid="ghl-location-input" aria-label="GoHighLevel Location ID"
          />
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
            Sub-Account Settings → Business Info
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Step name <span className="font-normal" style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <Input
            value={config?.name || ''}
            onChange={e => set({ name: e.target.value })}
            placeholder="Upsert to GoHighLevel"
            aria-label="GoHighLevel step name"
            className="h-9 text-sm"
            style={{ borderColor: 'var(--stroke)' }}
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Access token</label>
        <div className="flex items-center gap-2">
          <Input
            type={showToken ? 'text' : 'password'}
            value={config?.access_token || ''}
            onChange={e => set({ access_token: e.target.value.trim() })}
            placeholder="eyJhbGciOi…"
            className="h-9 text-sm flex-1"
            style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
            data-testid="ghl-token-input" aria-label="GoHighLevel access token"
          />
          <button type="button" onClick={() => setShowToken(v => !v)}
            className="text-xs px-2 h-9 rounded-md" style={{ color: 'var(--text-muted)', border: '1px solid var(--stroke)' }}>
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>
          Sub-Account Settings → Private Integrations (needs the <code>contacts.write</code> scope),
          or an OAuth access token. Never shown in run history.
        </p>
      </div>

      <div>
        <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>
          Field mapping
        </label>
        <p className="text-xs mb-2" style={{ color: 'var(--text-dim)' }}>
          Any contact field to any GoHighLevel parameter, including nested ones like{' '}
          <code>attributionSource.fbclid</code>.
        </p>
        {fieldMap.length > 0 && (
          <div className="space-y-2 mb-2">
            {fieldMap.map(m => {
              const isPreset = GHL_TARGET_PATHS.some(p => p.value === m.target);
              return (
                <div key={m.id} className="flex items-center gap-2 flex-wrap">
                  <button type="button"
                    onClick={() => updateMapping(m.id, { literal: !m.literal })}
                    className="text-[10px] font-bold uppercase tracking-wide px-2 h-9 rounded-md shrink-0"
                    style={{ border: '1px solid var(--stroke)', color: m.literal ? '#00A9A5' : 'var(--text-muted)', backgroundColor: m.literal ? 'rgba(0,169,165,0.06)' : 'transparent' }}
                    title="Toggle between a contact field and a fixed static value">
                    {m.literal ? 'Static' : 'Field'}
                  </button>
                  {m.literal ? (
                    <Input
                      value={m.source || ''}
                      onChange={e => updateMapping(m.id, { source: e.target.value })}
                      placeholder="static value"
                      className="h-9 text-sm w-40"
                      style={{ borderColor: 'var(--stroke)' }}
                      data-testid={`ghl-map-source-${m.id}`} aria-label="GoHighLevel mapping source"
                    />
                  ) : (
                    <Select value={m.source} onValueChange={v => updateMapping(m.id, { source: v })}>
                      <SelectTrigger className="h-9 text-sm w-44" style={{ borderColor: 'var(--stroke)' }} data-testid={`ghl-map-source-${m.id}`} aria-label="GoHighLevel mapping source">
                        <SelectValue placeholder="Tether field" />
                      </SelectTrigger>
                      <SelectContent className="sp-automation-select">
                        {CAPI_SOURCE_FIELDS.map(f => <SelectItem key={f.value} value={f.value} className="text-sm">{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <ArrowRight size={14} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                  <Select
                    value={isPreset ? m.target : '__custom__'}
                    onValueChange={v => updateMapping(m.id, { target: v === '__custom__' ? (isPreset ? '' : m.target) : v })}
                  >
                    <SelectTrigger className="h-9 text-sm w-56" style={{ borderColor: 'var(--stroke)' }} data-testid={`ghl-map-target-${m.id}`} aria-label="GoHighLevel mapping target">
                      <SelectValue placeholder="GHL field" />
                    </SelectTrigger>
                    <SelectContent className="sp-automation-select">
                      {GHL_TARGET_PATHS.map(p => <SelectItem key={p.value} value={p.value} className="text-sm">{p.label}</SelectItem>)}
                      <SelectItem value="__custom__" className="text-sm">Custom path…</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isPreset && (
                    <Input
                      value={m.target || ''}
                      onChange={e => updateMapping(m.id, { target: e.target.value })}
                      placeholder="customFields.myField"
                      className="h-9 text-sm w-56"
                      style={{ borderColor: 'var(--stroke)', fontFamily: 'var(--font-mono)' }}
                      data-testid={`ghl-map-target-custom-${m.id}`} aria-label="GoHighLevel mapping custom target path"
                    />
                  )}
                  <button onClick={() => removeMapping(m.id)}
                    className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
                    style={{ color: 'var(--text-dim)', backgroundColor: '#f8f6f2' }}
                    data-testid={`ghl-map-remove-${m.id}`} aria-label={`Remove GoHighLevel mapping to ${m.target || "unnamed field"}`}>
                    <X size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <button onClick={addMapping}
          className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"
          style={{ color: '#3867ef', backgroundColor: 'rgba(0,169,165,0.06)', border: '1px dashed rgba(0,169,165,0.25)' }}
          data-testid="ghl-map-add">
          <Plus size={11} /> Add mapping
        </button>
      </div>
    </div>
  );
}


function DelayEditor({ config, onChange }) {
  const seconds = config?.seconds ?? 60;

  return (
    <div>
      <p className="text-sm mb-3" style={{ color: 'var(--text-dim)' }}>
        Wait the specified time, then <strong>refetch the contact's latest data</strong> before proceeding.
        Useful for capturing phone numbers that arrive shortly after email.
      </p>
      <div className="flex items-center gap-3">
        <label className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Wait</label>
        <Input
          type="number"
          min="0"
          max="86400"
          value={seconds}
          onChange={e => onChange({ ...config, seconds: Math.max(0, parseInt(e.target.value) || 0) })}
          className="h-10 text-sm w-24"
          style={{ borderColor: 'var(--stroke)' }}
          data-testid="delay-seconds-input" aria-label="Delay in seconds"
        />
        <span className="text-sm" style={{ color: 'var(--text-dim)' }}>seconds</span>
        <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#f0f0ff', color: '#7a7b77' }}>
          {seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`}
        </span>
      </div>
    </div>
  );
}

/** Webhook Step Editor */
function WebhookEditor({ config, onChange }) {
  const excludeNullsId = useId();
  const url = config?.url || '';
  const name = config?.name || '';
  const fieldMap = config?.field_map || [];
  const excludeNulls = config?.exclude_nulls ?? true;  // Default to true

  const addMapping = () => {
    onChange({
      ...config,
      field_map: [...fieldMap, { id: uuid4(), source: 'email', target: '' }]
    });
  };

  const updateMapping = (id, patch) => {
    onChange({
      ...config,
      field_map: fieldMap.map(m => m.id === id ? { ...m, ...patch } : m)
    });
  };

  const removeMapping = (id) => {
    onChange({
      ...config,
      field_map: fieldMap.filter(m => m.id !== id)
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-bold uppercase tracking-wide mb-1.5 block" style={{ color: '#3867ef', opacity: 0.75 }}>
          Step Name <span className="font-normal">(optional)</span>
        </label>
        <Input
          value={name}
          onChange={e => onChange({ ...config, name: e.target.value })}
          placeholder="e.g. Send to GoHighLevel"
          className="h-10 text-sm"
          style={{ borderColor: 'var(--stroke)' }}
          data-testid="webhook-name-input" aria-label="Webhook step name"
        />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wide mb-1.5 block" style={{ color: '#3867ef', opacity: 0.75 }}>
          Webhook URL <span className="text-red-500">*</span>
        </label>
        <Input
          value={url}
          onChange={e => onChange({ ...config, url: e.target.value })}
          placeholder="https://hooks.zapier.com/hooks/catch/..."
          className="h-10 text-sm font-mono"
          style={{ borderColor: url ? 'var(--stroke)' : 'var(--red-error)', fontFamily: 'var(--font-mono)' }}
          data-testid="webhook-url-input" aria-label="Webhook URL"
        />
        {!url && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>Webhook URL is required</p>}
      </div>

      {/* Exclude null fields option */}
      <div className="sp-automation-check flex items-center gap-3 py-2 px-3 rounded-sm" style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke)' }}>
        <input
          type="checkbox"
          id={excludeNullsId}
          checked={excludeNulls}
          onChange={e => onChange({ ...config, exclude_nulls: e.target.checked })}
          className="w-4 h-4 rounded border accent-sky-600"
          data-testid="webhook-exclude-nulls"
        />
        <label htmlFor={excludeNullsId} className="text-sm cursor-pointer" style={{ color: 'var(--text-muted)' }}>
          <span className="font-semibold" style={{ color: '#3867ef' }}>Exclude null fields</span>
          <span className="text-xs ml-1.5" style={{ color: 'var(--text-dim)' }}>— Don't send fields that have no value</span>
        </label>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wide mb-1.5 block" style={{ color: '#3867ef', opacity: 0.75 }}>
          Field Mapping <span className="font-normal">(optional)</span>
        </label>
        <p className="text-xs mb-2" style={{ color: 'var(--text-dim)' }}>
          Map Tether fields to custom webhook field names. Leave empty to send all fields with default names.
        </p>
        {fieldMap.length > 0 && (
          <div className="space-y-2 mb-3">
            {fieldMap.map(m => (
              <div key={m.id} className="flex items-center gap-2">
                <Select value={m.source} onValueChange={v => updateMapping(m.id, { source: v })}>
                  <SelectTrigger className="h-9 text-sm w-44" style={{ borderColor: 'var(--stroke)', backgroundColor: '#f0f2fc' }} data-testid={`mapping-source-${m.id}`} aria-label="Webhook mapping source">
                    <SelectValue placeholder="Tether field" />
                  </SelectTrigger>
                  <SelectContent className="sp-automation-select">
                    {TETHER_FIELDS.map(f => <SelectItem key={f.value} value={f.value} className="text-sm">{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <ArrowRight size={14} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                <Input
                  value={m.target || ''}
                  onChange={e => updateMapping(m.id, { target: e.target.value })}
                  placeholder="webhook field name"
                  className="h-9 text-sm flex-1"
                  style={{ borderColor: '#D7E8A9', backgroundColor: '#FBFDF3' }}
                  data-testid={`mapping-target-${m.id}`} aria-label="Webhook mapping target"
                />
                <button
                  onClick={() => removeMapping(m.id)}
                  className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors hover:bg-red-50"
                  style={{ color: '#dc2626' }}
                  data-testid={`mapping-remove-${m.id}`} aria-label={`Remove webhook mapping to ${m.target || "unnamed field"}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={addMapping}
          className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"
          style={{ color: '#3867ef', backgroundColor: 'rgba(2,132,199,0.06)', border: '1px dashed rgba(2,132,199,0.25)' }}
          data-testid="add-field-mapping"
        >
          <Plus size={11} /> Add mapping
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────── Step Card ───────────────────────────

function StepCard({ step, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, onDuplicate }) {
  const typeInfo = STEP_TYPES.find(t => t.type === step.type) || STEP_TYPES[0];
  const Icon = typeInfo.icon;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const removeButtonRef = useRef(null);

  const renderEditor = () => {
    switch (step.type) {
      case 'wait_for': return <WaitForEditor config={step.config} onChange={c => onUpdate({ ...step, config: c })} />;
      case 'filter':   return <FilterEditor config={step.config} onChange={c => onUpdate({ ...step, config: c })} />;
      case 'delay':    return <DelayEditor config={step.config} onChange={c => onUpdate({ ...step, config: c })} />;
      case 'webhook':  return <WebhookEditor config={step.config} onChange={c => onUpdate({ ...step, config: c })} />;
      case 'capi':     return <CapiEditor config={step.config} onChange={c => onUpdate({ ...step, config: c })} />;
      case 'ghl':      return <GhlEditor config={step.config} onChange={c => onUpdate({ ...step, config: c })} />;
      default: return <p className="text-sm" style={{ color: 'var(--text-dim)' }}>Unknown step type</p>;
    }
  };

  return (
    <>
      <div
        className="sp-workflow-step rounded-sm border overflow-hidden"
        style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}
        data-testid={`step-card-${step.id}`}
      >
        {/* Step header */}
        <div
          className="flex items-center gap-3 px-5 py-4"
          style={{ backgroundColor: 'var(--surface-2)', borderBottom: '1px solid var(--stroke)' }}
        >
          {/* Step number */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
            style={{ backgroundColor: typeInfo.color }}
          >
            {index + 1}
          </div>

          {/* Icon */}
          <div
            className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${typeInfo.color}18` }}
          >
            <Icon size={17} style={{ color: typeInfo.color }} />
          </div>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold" style={{ color: typeInfo.color, fontFamily: 'var(--font)' }}>
              {typeInfo.label}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--text-dim)' }}>
              {typeInfo.desc}
            </div>
          </div>

          <div className="sp-workflow-step-actions">
          {/* Reorder buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onMoveUp}
              disabled={index === 0}
              className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
              style={{ color: 'var(--text-muted)' }}
              data-testid={`step-move-up-${step.id}`} aria-label={`Move step ${index + 1} up`}
              title="Move up"
            >
              <ChevronUp size={16} />
            </button>
            <button
              onClick={onMoveDown}
              disabled={index === total - 1}
              className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
              style={{ color: 'var(--text-muted)' }}
              data-testid={`step-move-down-${step.id}`} aria-label={`Move step ${index + 1} down`}
              title="Move down"
            >
              <ChevronDown size={16} />
            </button>
          </div>

          {/* Duplicate button */}
          <button
            onClick={onDuplicate}
            className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors hover:bg-blue-50"
            style={{ color: '#3867ef' }}
            data-testid={`step-duplicate-${step.id}`} aria-label={`Duplicate step ${index + 1}`}
            title="Duplicate step"
          >
            <Copy size={15} />
          </button>

          {/* Delete */}
          <button
            onClick={() => setConfirmDelete(true)}
            ref={removeButtonRef}
            className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors hover:bg-red-50"
            style={{ color: '#dc2626' }}
            data-testid={`step-remove-${step.id}`} aria-label={`Remove step ${index + 1}`}
            title="Delete step"
          >
            <Trash2 size={15} />
          </button>
          </div>
        </div>

        {/* Step content */}
        <div className="px-5 py-5">
          {renderEditor()}
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="sp-dialog sp-automation-dialog" onCloseAutoFocus={event => { event.preventDefault(); removeButtonRef.current?.focus(); }} style={{ backgroundColor: '#fff', borderColor: 'var(--stroke)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'var(--brand-navy)' }}>Remove Step?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              This {typeInfo.label} step will be removed from the automation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: 'var(--stroke)' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onRemove(); setConfirmDelete(false); }}
              style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none' }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─────────────────────────── Add Step Menu ───────────────────────────

function AddStepMenu({ onAdd }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full h-14 gap-3 text-base font-semibold border border-dashed"
          style={{ borderColor: 'var(--stroke)', color: '#20211f', backgroundColor: 'rgba(32,33,31,0.02)' }}
          data-testid="add-step-button"
        >
          <Plus size={18} /> Add Step
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="sp-automation-menu w-64" style={{ backgroundColor: '#fff', borderColor: 'var(--stroke)' }}>
        {STEP_TYPES.map(st => (
          <DropdownMenuItem
            key={st.type}
            onClick={() => onAdd(st.type)}
            className="flex items-center gap-3 py-3 px-4 cursor-pointer"
            data-testid={`add-step-${st.type}`}
          >
            <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${st.color}18` }}>
              <st.icon size={15} style={{ color: st.color }} />
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{st.label}</div>
              <div className="text-xs" style={{ color: 'var(--text-dim)' }}>{st.desc}</div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─────────────────────────── Main Page ───────────────────────────

export default function AutomationBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';

  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [trigger, setTrigger] = useState('lead_identified');
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState('');
  const [validationError, setValidationError] = useState(null);
  const editorRef = useRef(null);
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify({ name: '', enabled: true, trigger: 'lead_identified', steps: [] }));
  const formSnapshot = JSON.stringify({ name, enabled, trigger, steps });
  const hasChanges = formSnapshot !== savedSnapshot;
  const hydratedIdRef = useRef(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Test-as-contact: search real contacts and run one through the workflow
  const [testing, setTesting]           = useState(false);
  const testingRef = useRef(false);
  const [confirmTest, setConfirmTest] = useState(false);
  const testButtonRef = useRef(null);
  const [testResult, setTestResult]     = useState(null);
  const [testContact, setTestContact]   = useState(null);
  const [contactQuery, setContactQuery] = useState('');
  const [contactHits, setContactHits]   = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');
  const contactSearchRef = useRef(null);
  const contactResultsRef = useRef(null);
  const clearContactRef = useRef(null);
  useEffect(() => { if (testContact) clearContactRef.current?.focus(); }, [testContact]);

  useEffect(() => {
    const q = contactQuery.trim();
    let cancelled = false;
    setContactHits([]);
    setContactError('');
    setContactLoading(q.length >= 2);
    if (q.length < 2) return;
    const t = setTimeout(() => {
      fetch(`${API}/contacts?search=${encodeURIComponent(q)}`)
        .then(r => { if (!r.ok) throw new Error('Search failed'); return r.json(); })
        .then(list => { if (!cancelled) setContactHits((Array.isArray(list) ? list : [])
          .filter(c => c.email || c.name).slice(0, 8)); })
        .catch(() => { if (!cancelled) setContactError('Could not search contacts. Edit your search to try again.'); })
        .finally(() => { if (!cancelled) setContactLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [contactQuery]);

  const handleTest = async () => {
    if (testingRef.current || savingRef.current) return;
    if (isNew) { toast.error('Create the automation first, then test it'); return; }
    if (hasChanges) { toast.error('Save your changes first — the test runs the saved version'); return; }
    if (contactQuery.trim() && !testContact) return;
    testingRef.current = true;
    try {
      setTesting(true);
      setTestResult(null);
      const res = await fetch(`${API}/automations/${id}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: testContact?.contact_id || null }),
      });
      const data = await res.json();
      const result = res.ok ? data : {
        ...data, success: false,
        error: typeof data.detail === 'string' ? data.detail : data.error || `Test request failed (HTTP ${res.status}).`,
      };
      setTestResult(result);
      if (result.success) toast.success(result.steps ? 'Test workflow completed' : `Test sent → HTTP ${result.http_status}`);
      else toast.error(result.error || result.detail || `HTTP ${result.http_status}`);
    } catch (e) {
      setTestResult({ success: false, error: 'Test request failed. Check your connection and try again.' });
      toast.error('Test request failed');
    } finally {
      testingRef.current = false;
      setTesting(false);
      setConfirmTest(false);
    }
  };

  // Fetch existing automation for edit mode
  const { data: automation, isLoading, isError, error } = useQuery({
    queryKey: ['automation', id],
    queryFn: () => fetch(`${API}/automations/${id}`).then(r => {
      if (!r.ok) {
        if (r.status === 404) throw new Error('Automation not found');
        throw new Error('Failed to load automation');
      }
      return r.json();
    }),
    enabled: !isNew,
    retry: 1,
  });

  // Hydrate form when automation loads
  useEffect(() => {
    if (automation && hydratedIdRef.current !== id) {
      hydratedIdRef.current = id;
      setName(automation.name || '');
      setEnabled(automation.enabled ?? true);
      setTrigger(automation.trigger || 'lead_identified');
      let loadedSteps;
      // If automation has steps[], use them directly
      if (automation.steps?.length) {
        loadedSteps = automation.steps;
      } else {
        // Convert legacy fields to steps
        const legacySteps = [];
        // required_fields → wait_for step
        if (automation.required_fields?.length) {
          legacySteps.push({
            id: uuid4(),
            type: 'wait_for',
            config: { fields: automation.required_fields }
          });
        }
        // filters → filter step
        if (automation.filters?.length) {
          legacySteps.push({
            id: uuid4(),
            type: 'filter',
            config: { filters: automation.filters }
          });
        }
        // actions → webhook steps (with optional delay)
        const actions = automation.actions?.length
          ? automation.actions
          : automation.webhook_url
            ? [{ id: uuid4(), webhook_url: automation.webhook_url, field_map: automation.field_map || [] }]
            : [];
        actions.forEach(action => {
          if (action.delay_seconds > 0) {
            legacySteps.push({
              id: uuid4(),
              type: 'delay',
              config: { seconds: action.delay_seconds }
            });
          }
          legacySteps.push({
            id: uuid4(),
            type: 'webhook',
            config: {
              name: action.name || '',
              url: action.webhook_url || '',
              field_map: action.field_map || [],
              exclude_nulls: true, // Default for converted legacy automations
            }
          });
        });
        loadedSteps = legacySteps;
      }
      setSteps(loadedSteps);
      setSavedSnapshot(JSON.stringify({ name: automation.name || '', enabled: automation.enabled ?? true, trigger: automation.trigger || 'lead_identified', steps: loadedSteps }));
    }
  }, [automation, id]);

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Store pending navigation target for dialog
  const pendingTargetRef = useRef(null);
  const navigationOriginRef = useRef(null);

  // Safe navigation that checks for changes
  const safeNavigate = useCallback((path) => {
    if (hasChanges) {
      navigationOriginRef.current = document.activeElement;
      pendingTargetRef.current = path;
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  }, [hasChanges, navigate]);

  // Handle leaving after unsaved changes dialog
  const handleLeaveWithoutSaving = useCallback(() => {
    setShowUnsavedDialog(false);
    const target = pendingTargetRef.current || '/automations';
    pendingTargetRef.current = null;
    // Use setTimeout to ensure state updates are processed before navigation
    setTimeout(() => navigate(target), 0);
  }, [navigate]);

  // Handle staying on page
  const handleStay = useCallback(() => {
    setShowUnsavedDialog(false);
    pendingTargetRef.current = null;
  }, []);

  // Add a step
  const addStep = (type) => {
    const defaultConfig = {
      wait_for: { fields: ['email'] },
      filter: { filters: [] },
      delay: { seconds: 60 },
      webhook: { name: '', url: '', field_map: [], exclude_nulls: true },
      capi: { name: '', pixel_id: '', access_token: '', event_name: 'Purchase', test_event_code: '', combine_window: 0, action_source: 'website', event_source_url: '', field_map: [] },
      // Pre-filled to match GoHighLevel's own upsert example — access token
      // and location ID are the only two things left to fill in.
      ghl: { name: '', access_token: '', location_id: '', field_map: [
        { id: uuid4(), source: 'email',      target: 'email',      literal: false },
        { id: uuid4(), source: 'first_name', target: 'firstName',  literal: false },
        { id: uuid4(), source: 'last_name',  target: 'lastName',   literal: false },
        { id: uuid4(), source: 'phone',      target: 'phone',      literal: false },
        { id: uuid4(), source: 'fbclid',     target: 'attributionSource.fbclid',    literal: false },
        { id: uuid4(), source: 'client_ip',  target: 'attributionSource.ip',        literal: false },
        { id: uuid4(), source: 'user_agent', target: 'attributionSource.userAgent', literal: false },
      ] },
    };
    setSteps(prev => [...prev, { id: uuid4(), type, config: defaultConfig[type] }]);
  };

  // Update a step
  const updateStep = (stepId, updated) => {
    setSteps(prev => prev.map(s => s.id === stepId ? updated : s));
  };

  // Remove a step
  const removeStep = (stepId) => {
    setSteps(prev => prev.filter(s => s.id !== stepId));
  };

  // Move step up/down
  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    setSteps(prev => {
      const newSteps = [...prev];
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      return newSteps;
    });
  };

  // Duplicate a step
  const duplicateStep = (index) => {
    const stepToDuplicate = steps[index];
    const duplicatedStep = {
      ...JSON.parse(JSON.stringify(stepToDuplicate)), // Deep clone
      id: uuid4(), // New unique ID
    };
    // If webhook step, append "(copy)" to name if it has one
    if (duplicatedStep.type === 'webhook' && duplicatedStep.config?.name) {
      duplicatedStep.config.name = `${duplicatedStep.config.name} (copy)`;
    }
    setSteps(prev => {
      const newSteps = [...prev];
      newSteps.splice(index + 1, 0, duplicatedStep);
      return newSteps;
    });
    toast.success('Step duplicated');
  };

  // Validate before save
  const invalid = (message, fieldId, stepId) => {
    setValidationError({ message, fieldId, stepId });
    const scope = stepId
      ? [...(editorRef.current?.querySelectorAll('[data-testid]') || [])].find(el => el.dataset.testid === `step-card-${stepId}`)
      : editorRef.current;
    const field = [...(scope?.querySelectorAll('[data-testid]') || [])].find(el => el.dataset.testid === fieldId);
    field?.focus();
    toast.error(message);
    return false;
  };

  const validate = () => {
    setValidationError(null);
    if (!name.trim()) {
      return invalid('Please give your automation a name', 'automation-name-input');
    }
    if (steps.length === 0) {
      return invalid('Add at least one step to your automation', 'add-step-button');
    }
    // Need at least one action step (webhook, Facebook CAPI, or GoHighLevel)
    const webhookSteps = steps.filter(s => s.type === 'webhook');
    const capiSteps    = steps.filter(s => s.type === 'capi');
    const ghlSteps     = steps.filter(s => s.type === 'ghl');
    if (webhookSteps.length === 0 && capiSteps.length === 0 && ghlSteps.length === 0) {
      return invalid('Add at least one action step — Webhook, Facebook CAPI, or GoHighLevel', 'add-step-button');
    }
    // Validate each webhook URL
    for (const ws of webhookSteps) {
      const url = ws.config?.url?.trim() || '';
      if (!url) {
        return invalid('All Webhook steps must have a URL', 'webhook-url-input', ws.id);
      }
      try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname) throw new Error('Invalid URL');
      } catch {
        return invalid('Webhook steps need a valid http:// or https:// URL', 'webhook-url-input', ws.id);
      }
    }
    // Validate CAPI steps
    for (const cs of capiSteps) {
      if (!/^\d+$/.test(cs.config?.pixel_id || '')) {
        return invalid('Facebook CAPI steps need a numeric Pixel ID', 'capi-pixel-input', cs.id);
      }
      if (!(cs.config?.access_token || '').trim()) {
        return invalid('Facebook CAPI steps need an access token', 'capi-token-input', cs.id);
      }
    }
    // Validate GoHighLevel steps
    for (const gs of ghlSteps) {
      if (!(gs.config?.location_id || '').trim()) {
        return invalid('GoHighLevel steps need a Location ID', 'ghl-location-input', gs.id);
      }
      if (!(gs.config?.access_token || '').trim()) {
        return invalid('GoHighLevel steps need an access token', 'ghl-token-input', gs.id);
      }
    }
    // Check wait_for has at least one field or tag
    for (const waitFor of steps.filter(s => s.type === 'wait_for')) {
      if (!(waitFor.config?.fields?.length) && !(waitFor.config?.tags?.length)) {
        return invalid('Wait For step must have at least one field or tag selected', 'wait-for-field-email', waitFor.id);
      }
    }
    // Check filter values are not empty when required
    for (const step of steps) {
      if (step.type === 'delay') {
        const seconds = Number(step.config?.seconds ?? 60);
        if (!Number.isInteger(seconds) || seconds < 0 || seconds > 86400) {
          return invalid('Delay must be a whole number from 0 to 86400 seconds', 'delay-seconds-input', step.id);
        }
      }
      if (step.type === 'filter') {
        const filters = step.config?.filters || [];
        for (const f of filters) {
          const needsValue = !['exists', 'not_exists'].includes(f.operator);
          if (needsValue && !f.value?.trim()) {
            return invalid('Filter conditions with equals/contains require a value', `filter-value-${f.id}`, step.id);
          }
        }
      }
    }
    return true;
  };

  // Save automation
  const handleSave = async () => {
    if (savingRef.current || testingRef.current) return;
    if (!validate()) return;

    savingRef.current = true;
    setSaveError('');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        enabled,
        trigger,
        steps: steps,
        // Clear legacy fields when using steps
        required_fields: [],
        filters: [],
        actions: [],
        webhook_url: null,
        field_map: [],
      };

      const method = isNew ? 'POST' : 'PUT';
      const url = isNew ? `${API}/automations` : `${API}/automations/${id}`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(typeof detail?.detail === 'string' ? detail.detail : `Unable to save (HTTP ${res.status}). Please try again.`);
      }
      const saved = await res.json();
      if (saved?.id) qc.setQueryData(['automation', saved.id], saved);

      setSavedSnapshot(formSnapshot);
      toast.success(isNew ? 'Automation created — run a test below' : 'Automation saved!');
      qc.invalidateQueries({ queryKey: ['automations'] });
      // Stay on the builder so the automation can be tested right away;
      // a fresh create swaps the URL to its edit route.
      if (isNew && saved?.id) navigate(`/automations/builder/${saved.id}`, { replace: true });
    } catch (e) {
      setSaveError(`Failed to save: ${e.message}`);
      toast.error(`Failed to save: ${e.message}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Loading state
  if (!isNew && isLoading) {
    return (
      <div className="sp-workflow-editor">
        <Skeleton className="h-12 w-64 mb-6" />
        <Skeleton className="h-40 w-full mb-4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Error state
  if (!isNew && isError) {
    return (
      <div className="sp-workflow-editor">
        <button
          onClick={() => navigate('/automations')}
          className="flex items-center gap-2 text-sm font-semibold mb-4 transition-colors hover:opacity-80"
          style={{ color: 'var(--brand-navy)' }}
        >
          <ArrowLeft size={16} /> Back to Automations
        </button>
        <div className="rounded-sm border border-dashed p-8 text-center" style={{ borderColor: 'var(--stroke)', backgroundColor: 'var(--surface-2)' }}>
          <AlertTriangle size={48} className="mx-auto mb-4" style={{ color: '#dc2626' }} />
          <h3 className="text-[13px] font-medium mb-2" style={{ color: 'var(--red-error)' }}>
            Failed to load automation
          </h3>
          <p className="text-sm" style={{ color: '#b91c1c' }}>
            {error?.message || 'An unexpected error occurred'}
          </p>
          <Button
            onClick={() => navigate('/automations')}
            className="mt-4 h-10 px-6 text-sm font-semibold text-white"
            style={{ backgroundColor: 'var(--ink)' }}
          >
            Return to Automations
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-workflow-editor" ref={editorRef}>
      <AlertDialog open={confirmTest} onOpenChange={open => { if (!testingRef.current) setConfirmTest(open); }}>
        <AlertDialogContent className="sp-dialog sp-automation-dialog" onCloseAutoFocus={event => { event.preventDefault(); testButtonRef.current?.focus(); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Run a live test?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends real data to the saved workflow’s destinations, including webhooks, Meta and GoHighLevel.
              {testContact ? ` Contact: ${testContact.email || testContact.name}.` : ' Uses the latest identified contact (or sample data if none exists); field and filter gates are bypassed.'}
              {' '}Delays are skipped. This is not a dry run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={testing}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={testing} aria-busy={testing} onClick={e => { e.preventDefault(); handleTest(); }}>
              {testing ? 'Testing…' : 'Run test'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Unsaved changes dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={(open) => { if (!open) handleStay(); }}>
        <AlertDialogContent className="sp-dialog sp-automation-dialog" onCloseAutoFocus={event => { event.preventDefault(); navigationOriginRef.current?.focus(); }} style={{ backgroundColor: '#fff', borderColor: 'var(--stroke)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'var(--brand-navy)' }}>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              You have unsaved changes to this automation. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleStay}
              style={{ borderColor: 'var(--stroke)' }}
            >
              Stay
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleLeaveWithoutSaving}
              style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none' }}
              data-testid="leave-without-saving-btn"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <button type="button" onClick={() => safeNavigate('/automations')} className="sp-workflow-back" data-testid="back-to-automations"><ArrowLeft size={15} /> Workflow library</button>
      <PageIntro title={isNew ? 'Create automation' : 'Edit automation'}
        actions={<div className="row"><Status tone={hasChanges ? 'amber' : 'quiet'}>{hasChanges ? 'Unsaved changes' : isNew ? 'New workflow' : 'Saved workflow'}</Status><label className="sp-workflow-enabled">{enabled ? 'Active' : 'Paused'}<Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Enable automation" data-testid="automation-enabled-toggle" /></label></div>} />
      <label className="sp-workflow-name">Workflow name<Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Send purchases to Meta" className="sp-input" data-testid="automation-name-input" aria-invalid={validationError?.fieldId === 'automation-name-input' || undefined} aria-describedby={validationError?.fieldId === 'automation-name-input' ? 'automation-validation-error' : undefined} /></label>
      {validationError && <p id="automation-validation-error" data-testid="automation-validation-error" className="sp-automation-error" role="alert">{validationError.message}</p>}

      {/* Trigger indicator */}
      <div
        className="sp-workflow-trigger rounded-sm border mb-6 overflow-hidden"
        style={{ borderColor: 'var(--stroke)', backgroundColor: 'rgba(32,33,31,0.02)' }}
      >
        <div
          className="flex items-center gap-3 px-5 py-4"
          style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke)' }}
        >
          <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(32,33,31,0.12)' }}>
            <Activity size={17} style={{ color: '#20211f' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold mb-1.5" style={{ color: '#20211f', fontFamily: 'var(--font)' }}>Trigger</div>
            <Select value={trigger} onValueChange={setTrigger}>
              <SelectTrigger className="h-9 text-sm w-full max-w-md bg-white" style={{ borderColor: 'var(--stroke)' }} data-testid="trigger-select" aria-label="Automation trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="sp-automation-select">
                <SelectItem value="lead_identified" className="text-sm">
                  New lead identified — fires when a contact's email or phone is first captured
                </SelectItem>
                <SelectItem value="purchase" className="text-sm">
                  Purchase received — fires per sale, only when it's matched to a journey
                </SelectItem>
                <SelectItem value="purchase_any" className="text-sm">
                  Purchase received (any) — fires for every sale, matched or not
                </SelectItem>
                <SelectItem value="renewal" className="text-sm">
                  Renewal received — a recurring payment on a subscription, matched to a journey
                </SelectItem>
                <SelectItem value="refund" className="text-sm">
                  Refund or chargeback — money returned on a matched sale
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] mt-1.5" style={{ color: 'var(--text-dim)' }}>
              {trigger === 'purchase'
                ? 'Ideal for sending attributed conversions to Facebook CAPI — unmatched sales never fire it.'
                : trigger === 'purchase_any'
                  ? 'Every sale goes through — even ones Tether couldn\'t match to a journey (those carry the sale\'s own email/name, no attribution).'
                  : 'The classic flow: enrich and forward new leads to your destinations.'}
            </div>
          </div>
        </div>
      </div>

      {/* Connector */}
      {steps.length > 0 && (
        <div className="flex justify-center py-2">
          <div className="w-0.5 h-6 rounded-full" style={{ backgroundColor: 'var(--lime-border)' }} />
        </div>
      )}

      {/* Steps with animation */}
      <div className="space-y-0">
        <AnimatePresence mode="popLayout">
          {steps.map((step, idx) => (
            <motion.div
              key={step.id}
              layout
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
              transition={{ 
                type: 'spring',
                stiffness: 500,
                damping: 30,
                mass: 1,
              }}
            >
              <StepCard
                step={step}
                index={idx}
                total={steps.length}
                onUpdate={updated => updateStep(step.id, updated)}
                onRemove={() => removeStep(step.id)}
                onMoveUp={() => moveStep(idx, -1)}
                onMoveDown={() => moveStep(idx, 1)}
                onDuplicate={() => duplicateStep(idx)}
              />
              {/* Connector line between steps */}
              {idx < steps.length - 1 && (
                <motion.div 
                  layout
                  className="flex justify-center py-2"
                >
                  <div className="w-0.5 h-6 rounded-full" style={{ backgroundColor: 'var(--lime-border)' }} />
                </motion.div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add step button */}
      <div className="mt-6">
        <AddStepMenu onAdd={addStep} />
      </div>

      {/* Empty state hint */}
      {steps.length === 0 && (
        <div className="mt-8 rounded-sm border border-dashed p-8 text-center" style={{ borderColor: 'var(--stroke)', backgroundColor: 'rgba(32,33,31,0.02)' }}>
          <div className="w-14 h-14 rounded-sm flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(32,33,31,0.08)' }}>
            <Zap size={24} style={{ color: '#20211f' }} />
          </div>
          <h2 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font)', color: 'var(--brand-navy)' }}>
            Build your automation pipeline
          </h2>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
            Add steps to control when and how leads are sent to your webhook. Start with a <strong>Wait For</strong> step to ensure required fields are captured, add <strong>Filters</strong> to target specific leads, and configure <strong>Webhook</strong> steps to send data to your CRM or other services.
          </p>
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div className="mt-6 rounded-sm border overflow-hidden"
          style={{ backgroundColor: testResult.success ? '#ecfdf5' : '#fef2f2', borderColor: testResult.success ? '#a7f3d0' : '#fecaca' }}
          data-testid="test-result" role={testResult.success ? 'status' : 'alert'}>
          <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
            {testResult.success
              ? <CheckCircle2 size={16} style={{ color: '#059669' }} />
              : <AlertTriangle size={16} style={{ color: '#dc2626' }} />}
            <span className="text-sm font-bold flex-1" style={{ color: testResult.success ? '#065f46' : '#991b1b' }}>
              {testResult.success ? (testResult.steps ? 'Workflow completed ✔' : 'Webhook received ✔')
                : testResult.gated ? 'Workflow skipped this contact'
                : testResult.steps ? 'Workflow finished with problems'
                : testResult.http_status == null ? 'Connection failed'
                : `Failed — HTTP ${testResult.http_status}`}
            </span>
            {testResult.contact?.email && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.05)', color: testResult.success ? '#065f46' : '#991b1b' }}>
                as {testResult.contact.email}
              </span>
            )}
            {testResult.http_status && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: testResult.success ? '#d1fae5' : '#fee2e2', color: testResult.success ? '#065f46' : '#991b1b' }}>
                HTTP {testResult.http_status}
              </span>
            )}
            {testResult.duration_ms !== undefined && (
              <span className="text-xs" style={{ color: testResult.success ? '#065f46' : '#991b1b', opacity: 0.7 }}>{testResult.duration_ms}ms</span>
            )}
          </div>
          {testResult.steps && (
            <div className="px-4 pb-3 space-y-1.5" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 10 }}>
              {testResult.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs min-w-0">
                  {s.success
                    ? <CheckCircle2 size={12} className="shrink-0" style={{ color: '#059669' }} />
                    : <AlertTriangle size={12} className="shrink-0" style={{ color: '#dc2626' }} />}
                  <span className="font-bold uppercase tracking-wide shrink-0" style={{ width: 62, color: 'var(--text-muted)', fontSize: 10 }}>
                    {s.type}
                  </span>
                  <span className="font-medium truncate" style={{ color: s.success ? '#065f46' : '#991b1b' }}>
                    {s.name || s.event || s.url || ''}
                  </span>
                  {s.http_status != null && (
                    <span className="font-bold shrink-0">HTTP {s.http_status}</span>
                  )}
                  {s.duration_ms != null && <span className="shrink-0" style={{ opacity: 0.6 }}>{s.duration_ms}ms</span>}
                  {s.note && <span className="truncate" style={{ color: 'var(--text-dim)' }}>{s.note}</span>}
                </div>
              ))}
            </div>
          )}
          {testResult.error && (
            <div className="px-4 pb-3">
              <code className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: '#991b1b' }}>{testResult.error}</code>
            </div>
          )}
          {!testResult.gated && testResult.payload && (
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="pt-3">
                <p className="text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#20211f', opacity: 0.65 }}>Payload sent</p>
                <pre className="text-xs overflow-auto rounded-sm p-2.5 max-h-44" style={{ backgroundColor: 'rgba(0,0,0,0.04)', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>
                  {JSON.stringify(testResult.payload, null, 2)}
                </pre>
              </div>
              <div className="pt-3">
                <p className="text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#20211f', opacity: 0.65 }}>Response</p>
                <pre className="text-xs overflow-auto rounded-sm p-2.5 max-h-44" style={{ backgroundColor: 'rgba(0,0,0,0.04)', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>
                  {(() => { try { return JSON.stringify(JSON.parse(testResult.response_body), null, 2); } catch { return testResult.response_body || '(empty — normal for n8n)'; } })()}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {saveError && <p role="alert" className="sp-automation-error" data-testid="automation-save-error">{saveError}</p>}

      {/* Footer actions */}
      <div
        className="sp-workflow-footer"
        style={{ backgroundColor: '#fff', borderColor: 'var(--stroke)' }}
      >
        {/* Test as a specific contact (default: latest identified) */}
        <div className="relative min-w-0" style={{ width: 230 }}>
          {testContact ? (
            <div className="flex items-center gap-1.5 h-10 px-3 rounded-sm text-xs font-medium"
              style={{ border: '1px solid #059669', backgroundColor: 'rgba(5,150,105,0.06)', color: '#065f46' }}>
              <span className="truncate flex-1">{testContact.email || testContact.name}</span>
              <button type="button" ref={clearContactRef} aria-label="Use latest contact instead" onClick={() => setTestContact(null)} title="Back to latest contact" style={{ color: '#059669' }}>
                <X size={12} />
              </button>
            </div>
          ) : (
            <Input
              ref={contactSearchRef}
              value={contactQuery}
              onChange={e => setContactQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'ArrowDown' && contactHits.length) { e.preventDefault(); contactResultsRef.current?.querySelector('button')?.focus(); }
                if (e.key === 'Escape') { e.preventDefault(); setContactHits([]); }
              }}
              placeholder="Test as… (latest contact)"
              className="h-10 text-xs"
              style={{ borderColor: 'var(--stroke)' }}
              aria-label="Search test contact" data-testid="test-contact-search"
            />
          )}
          {!testContact && contactHits.length > 0 && (
            <div ref={contactResultsRef} aria-label="Matching test contacts" className="absolute bottom-[52px] left-0 right-0 rounded-sm border shadow-lg overflow-hidden z-50"
              style={{ backgroundColor: '#ffffff', borderColor: 'var(--stroke)' }}>
              {contactHits.map((c, index) => (
                <button key={c.contact_id} type="button"
                  onClick={() => { setTestContact({ contact_id: c.contact_id, email: c.email, name: c.name }); setContactQuery(''); setContactHits([]); }}
                  onKeyDown={e => {
                    const buttons = contactResultsRef.current?.querySelectorAll('button');
                    if (e.key === 'ArrowDown') { e.preventDefault(); buttons?.[(index + 1) % contactHits.length]?.focus(); }
                    if (e.key === 'ArrowUp') { e.preventDefault(); if (index === 0) contactSearchRef.current?.focus(); else buttons?.[index - 1]?.focus(); }
                    if (e.key === 'Escape') { e.preventDefault(); setContactHits([]); contactSearchRef.current?.focus(); }
                  }}
                  className="w-full text-left px-3 py-2 text-xs truncate block transition-colors hover:bg-[#f4f1ea]">
                  <span className="font-semibold">{c.email || c.name || (c.contact_id || '').slice(0, 8)}</span>
                  {c.name && c.email && <span style={{ color: 'var(--text-dim)' }}> — {c.name}</span>}
                </button>
              ))}
            </div>
          )}
          {contactLoading && <span role="status" className="text-xs">Searching contacts…</span>}
          {contactError && <p role="alert" className="sp-automation-error">{contactError}</p>}
          {!testContact && contactQuery.trim().length >= 2 && !contactLoading && !contactError && contactHits.length === 0 && <p role="status" className="text-xs">No contacts found. Clear the search to use the latest contact.</p>}
        </div>
        <Button
          variant="outline"
          onClick={() => { if (!savingRef.current && !testingRef.current) setConfirmTest(true); }}
          ref={testButtonRef}
          disabled={testing || saving || isNew || hasChanges || (!testContact && !!contactQuery.trim())}
          aria-busy={testing}
          className="gap-2 h-10 px-5 text-sm font-semibold"
          style={{ borderColor: '#059669', color: '#059669', opacity: (isNew || hasChanges) ? 0.5 : 1 }}
          title={isNew ? 'Create the automation first' : hasChanges ? 'Save changes first — the test runs the saved version' : 'Run this contact through the workflow'}
          data-testid="test-automation-button"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
          {testing ? 'Testing…' : 'Test'}
        </Button>

        <div className="flex-1" />

        <Button
          variant="outline"
          onClick={() => safeNavigate('/automations')}
          className="h-10 px-6 text-sm font-semibold"
          style={{ borderColor: 'var(--stroke)', color: 'var(--text-muted)' }}
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || testing}
          aria-busy={saving}
          className="gap-2 h-10 px-8 text-sm font-semibold text-white"
          style={{ backgroundColor: 'var(--ink)' }}
          data-testid="save-automation-button"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving…' : isNew ? 'Create Automation' : 'Save Changes'}
        </Button>
      </div>


    </div>
  );
}
